import {
    DeleteOutlined,
    EditOutlined,
    EyeOutlined,
    HolderOutlined,
    MoreOutlined,
    RetweetOutlined,
} from "@ant-design/icons";
import { Button, Dropdown, Flex, MenuProps } from "antd";
import { FC, memo, ReactNode, useMemo } from "react";
import { Draggable } from "react-beautiful-dnd";
import { Market, Schedule } from "tm-shared/types";

import { MarketCard } from "@/components/MarketCard";
import { PrimaryText, SecondaryText } from "@/components/Typography/Styled";

import {
    ScheduleMenuAction,
    ScheduleMenuConfig,
} from "../../RouteEditor.types";
import { StyledScheduleCard } from "./Styled";

interface ScheduleCardProps {
    schedule: Schedule;
    index: number;
    market: Market;
    isSelected: boolean;
    multiSelectMode: boolean;
    handleSelectSchedule: (item: Schedule) => void;
    handleTap: () => void;
    scheduleMenuConfig: ScheduleMenuConfig[];
}

const IconsMap: Record<ScheduleMenuAction, ReactNode> = {
    view: <EyeOutlined />,
    edit: <EditOutlined />,
    move: <RetweetOutlined />,
    delete: <DeleteOutlined />,
};

export const ScheduleCard: FC<ScheduleCardProps> = memo(
    ({
        schedule,
        index,
        market,
        isSelected,
        multiSelectMode,
        handleSelectSchedule,
        handleTap,
        scheduleMenuConfig,
    }) => {
        const menuItems: MenuProps["items"] = useMemo(() => {
            return scheduleMenuConfig.map(({ type, disabled, label }) => ({
                key: type,
                label,
                disabled: disabled?.(schedule),
                icon: IconsMap[type],
                danger: type === "delete",
            }));
        }, [schedule, scheduleMenuConfig]);

        const actionsMap = scheduleMenuConfig.reduce(
            (acc, { type, onClick }) => ({ ...acc, [type]: onClick }),
            {},
        );

        const handleMenuClick: MenuProps["onClick"] = (event) => {
            actionsMap[event.key]?.(schedule);
        };

        return (
            <Draggable
                key={schedule.marketId}
                draggableId={schedule.marketId}
                index={index}
                isDragDisabled={multiSelectMode}
            >
                {(provided) => {
                    return (
                        <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                        >
                            <StyledScheduleCard
                                $hasDebt={market?.hasDebt}
                                $selected={isSelected}
                                onTouchStart={handleTap}
                                onClick={() => handleSelectSchedule(schedule)}
                            >
                                {!multiSelectMode && (
                                    <Button
                                        style={{ width: 16, marginRight: 8 }}
                                        icon={<HolderOutlined />}
                                        {...provided.dragHandleProps}
                                        type="text"
                                    />
                                )}

                                <Flex
                                    vertical
                                    align="flex-start"
                                    style={{ marginRight: "auto" }}
                                >
                                    <MarketCard
                                        address={market?.address?.name || "-"}
                                        fop={market?.contragent?.name || "-"}
                                    />
                                    <Flex gap={4}>
                                        <SecondaryText>
                                            Разів на тиждень:
                                        </SecondaryText>

                                        <PrimaryText>
                                            {schedule?.days?.length || 0}
                                        </PrimaryText>
                                    </Flex>
                                </Flex>

                                {!multiSelectMode && (
                                    <Dropdown
                                        trigger={["click"]}
                                        menu={{
                                            items: menuItems,
                                            onClick: (e) => handleMenuClick(e),
                                        }}
                                    >
                                        <Button
                                            icon={<MoreOutlined />}
                                            type="text"
                                            style={{ width: 24 }}
                                        />
                                    </Dropdown>
                                )}
                            </StyledScheduleCard>
                        </div>
                    );
                }}
            </Draggable>
        );
    },
);

ScheduleCard.displayName = "ScheduleCard";
